import {
  type CallWindowLimit,
  type CallWindowLimitFilter,
  type CallWindowLimitList,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type WindowLimitOverride } from '../../tools/callWindowLimits';
import { CallWindowLimitClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export const useCallWindowLimitBatchQuery = ({
  filter,
  enabled = true,
  options,
}: {
  filter: CallWindowLimitFilter;
  enabled?: boolean;
  options?: Partial<UseQueryOptions<CallWindowLimitList, Error>>;
}) => {
  return useQuery({
    queryFn: async () => {
      return await CallWindowLimitClientService.loadCallWindowLimits({ filter });
    },
    queryKey: [
      queryKeys.callWindowLimit.root,
      queryKeys.callWindowLimit.list,
      queryKeys.callWindowLimit.getHash(filter),
    ],
    enabled,
    ...options,
  });
};

/**
 * Active limit overrides keyed by call_window_id, for reading a window's effective limits as
 * `override?.windowCount ?? window.windowCount`.
 *
 * Not filtered by department: an override only ever exists for a department-owned window, so
 * the call_window_id already identifies the department. Callers share one fetch (same query
 * key), so this is safe to call from several components on the same screen.
 */
export const useWindowLimitOverrides = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data } = useCallWindowLimitBatchQuery({ filter: { isActive: true }, enabled });

  return useMemo(() => {
    const map: Record<number, WindowLimitOverride> = {};
    (data?.results ?? []).forEach((l) => {
      map[l.callWindowId] = { windowCount: l.windowCount, maintMax: l.maintMax };
    });
    return map;
  }, [data]);
};

export const useCallWindowLimitCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      limit: Omit<
        CallWindowLimit,
        'id' | 'createdAt' | 'updatedAt' | 'fieldMask' | 'pageNumber' | 'orderBy' | 'orderDir'
      >,
    ) => {
      const req = {
        id: 0,
        createdAt: '',
        updatedAt: '',
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
        ...limit,
      };
      return await CallWindowLimitClientService.Create(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.callWindowLimit.root] });
    },
  });
};

export const useCallWindowLimitUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (limit: CallWindowLimit) => {
      return await CallWindowLimitClientService.Update(limit);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.callWindowLimit.root] });
    },
  });
};

export const useCallWindowLimitDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const req = {
        id,
        callWindowId: 0,
        windowCount: 0,
        maintMax: -1,
        createdAt: '',
        updatedAt: '',
        isActive: true,
        fieldMask: [],
        pageNumber: 0,
        orderBy: '',
        orderDir: '',
      };
      return await CallWindowLimitClientService.Delete(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.callWindowLimit.root] });
    },
  });
};
